/**
 * useContracts Hook - T164
 *
 * TanStack Query hooks for contract management
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type {
  CreateContractInput,
  UpdateContractInput,
  ListContractsQuery,
  ContractStatus,
  ContractBillingInterval,
} from '@armard/shared';

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export interface ContractItem {
  id: string;
  contractId: string;
  position: number;
  description: string;
  quantity: string;
  unit: string | null;
  unitPrice: string;
  longDescription: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Contract {
  id: string;
  contractNumber: string;
  title: string;
  description: string | null;
  customerId: string;
  status: ContractStatus;
  startDate: string;
  endDate: string | null;
  terminationDate: string | null;
  billingInterval: ContractBillingInterval;
  billingDayOfMonth: number;
  nextBillingDate: string | null;
  lastBillingDate: string | null;
  vatRate: string;
  customerReference: string | null;
  invoicePrefix: string | null;
  autoGenerate: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
  customer?: {
    id: string;
    customerNumber: string;
    companyName: string;
  };
  items: ContractItem[];
}

export interface ContractListResponse {
  data: Contract[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Re-export types from shared
export type { ContractStatus, ContractBillingInterval };

// Status helpers
export const statusLabels: Record<ContractStatus, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  paused: 'Pausiert',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

export const statusColors: Record<ContractStatus, string> = {
  draft: 'bg-white/10 text-white/60',
  active: 'bg-green-500/20 text-green-400',
  paused: 'bg-yellow-500/20 text-yellow-400',
  completed: 'bg-blue-500/20 text-blue-400',
  cancelled: 'bg-red-500/20 text-red-400',
};

export const billingIntervalLabels: Record<ContractBillingInterval, string> = {
  monthly: 'Monatlich',
  quarterly: 'Vierteljährlich',
  semiannually: 'Halbjährlich',
  annually: 'Jährlich',
};

// ─────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────

export const contractKeys = {
  all: ['contracts'] as const,
  lists: () => [...contractKeys.all, 'list'] as const,
  list: (query: ListContractsQuery) => [...contractKeys.lists(), query] as const,
  details: () => [...contractKeys.all, 'detail'] as const,
  detail: (id: string) => [...contractKeys.details(), id] as const,
};

// ─────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to list contracts with filtering and pagination
 */
export function useContracts(query: Partial<ListContractsQuery> = {}) {
  const params = new URLSearchParams();

  if (query.page) params.append('page', String(query.page));
  if (query.limit) params.append('limit', String(query.limit));
  if (query.status) params.append('status', query.status);
  if (query.customerId) params.append('customerId', query.customerId);
  if (query.search) params.append('search', query.search);
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortDirection) params.append('sortDirection', query.sortDirection);

  const queryString = params.toString();

  return useQuery({
    queryKey: contractKeys.list(query as ListContractsQuery),
    queryFn: () =>
      api.get<ContractListResponse>(`/contracts${queryString ? `?${queryString}` : ''}`),
  });
}

/**
 * Hook to get a single contract by ID
 */
export function useContract(id: string | undefined) {
  return useQuery({
    queryKey: contractKeys.detail(id!),
    queryFn: () => api.get<Contract>(`/contracts/${id}`),
    enabled: !!id,
  });
}

/**
 * Hook to create a new contract
 */
export function useCreateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContractInput) =>
      api.post<Contract>('/contracts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
    },
  });
}

/**
 * Hook to update a contract
 */
export function useUpdateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContractInput }) =>
      api.patch<Contract>(`/contracts/${id}`, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.setQueryData(contractKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to delete a contract
 */
export function useDeleteContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/contracts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
    },
  });
}

/**
 * Hook to generate an invoice from a contract
 */
export function useGenerateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contractId: string) =>
      api.post<{ id: string }>(`/contracts/${contractId}/generate-invoice`, {}),
    onSuccess: (_, contractId) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(contractId) });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * Hook to activate a contract
 */
export function useActivateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<Contract>(`/contracts/${id}/activate`, {}),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.setQueryData(contractKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to pause a contract
 */
export function usePauseContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<Contract>(`/contracts/${id}/pause`, {}),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.setQueryData(contractKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to cancel a contract
 */
export function useCancelContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, terminationDate }: { id: string; terminationDate?: string }) =>
      api.post<Contract>(`/contracts/${id}/cancel`, { terminationDate }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.setQueryData(contractKeys.detail(data.id), data);
    },
  });
}
